import type React from 'react';
import { useEffect, useMemo } from 'react';

import { ResourcesContext } from '@ragu-components/ord-components';
import {
  useCreateOrdDataProvider,
  useCreateOrdAuthProvider,
  getRealmId,
  createI18nProviderWithBrowserLocale,
  actionsFromPermission,
  columnActionsFromPermission,
  contactFavoritesCallback,
  SESSION_PATH,
  CSRF_TOKEN_PATH,
} from '@ragu-components/ord-providers';
import { CalendarEvents, Resources } from '@ragu-components/ord-resources';
import { Admin, CustomRoutes } from 'react-admin';
import { RouterProvider, createBrowserRouter, useNavigate } from 'react-router-dom';

import useVisitedResources from 'src/hooks/useVisitedResources';
import { getApTemplateDefaults } from 'src/providers/dataProvider/apTemplates';
import {
  apGroupCallbacks,
  sipIdentityCallbacks,
  callflowCallbacks,
  calendarEventCallbacks,
  personalCalendarEventCallbacks,
} from 'src/providers/dataProvider/callbacks';
import { createCalendarEventsDataProvider } from 'src/providers/dataProvider/createCalendarEventsDataProvider';
import { createImportDataProvider } from 'src/providers/dataProvider/createImportDataProvider';
import { createTasksDataProvider } from 'src/providers/dataProvider/createTasksDataProvider';
import {
  getTotp,
  createTotp,
  updatePassword,
  totpVerify,
  downloadFile,
  getUserSettings,
  updateUserSettings,
  getUserVoicemail,
  createUserVoicemail,
  updateUserVoicemail,
  getPersonalVoicemail,
  updatePersonalVoicemail,
  updateAutoprovisioningAuthentication,
  updateAutoprovisioningSettings,
  createBackup,
  createBackupImport,
  createBackupRestore,
} from 'src/providers/dataProvider/settings';
import { getMessages } from 'src/providers/i18nProvider';
import { LoginPage } from 'src/views/LoginPage';
import { RESOURCES } from 'src/views/Resources';

import { HomePage } from '../homePage';

import { ROUTES } from './Routes';

import type { Session } from '@ragu-components/ord-types';
import type { Permission as RbacPermission } from '@ratto-components/ra-rbac';
import type { AxiosInstance } from 'axios';
import type { AdminProps } from 'react-admin';
import type { NavigateFunction } from 'react-router-dom';
import type { IngordDataProvider } from 'src/lib/types';

const ALL_RESOURCES = Object.values(RESOURCES);
const ROUTES_ELEMENT = <CustomRoutes>{ROUTES}</CustomRoutes>;

const DEFAULT_SESSION_REFRESH_INTERVAL_MS = 1000 * 3600; // 1 hour

declare global {
  interface Window {
    ordReactRouterNavigate: NavigateFunction;
  }
}

/**
 * Helper function to expose the `navigate` function from `react-router-dom` to the global `window` object.
 *
 * @return {null} Returns `null` since this function does not render anything.
 */
const NavigateHelper = (): null => {
  const navigate = useNavigate();
  useVisitedResources();
  useEffect(() => {
    window.ordReactRouterNavigate = navigate;
  }, [navigate]);
  return null;
};

const getCalendarPermissions = (session: Session): RbacPermission[] => {
  return session.permission.calendars != null
    ? [
        { action: actionsFromPermission(session.permission.calendars), resource: CalendarEvents },
        { action: columnActionsFromPermission(session.permission.calendars), resource: `${CalendarEvents}.*` },
      ]
    : [];
};

const getRolesPermissions = (session: Session): RbacPermission[] => {
  const isAdmin = session.acl_labels.some((l) => ['all', 'admin'].includes(l.name));
  const rolesPermissions: RbacPermission[] = [
    {
      action: ['list', 'create', 'delete', 'edit'],
      resource: 'roles',
    },
    {
      action: '*',
      resource: 'roles*',
    },
  ];
  return isAdmin ? rolesPermissions : [];
};

const getAdditionalPermissions = (session: Session): RbacPermission[] => [
  ...getCalendarPermissions(session),
  ...getRolesPermissions(session),
];

export const App = ({ loginPage = <LoginPage />, ...props }: AdminProps): React.ReactElement => {
  const authProvider = useCreateOrdAuthProvider({
    sessionPath: SESSION_PATH,
    csrfTokenPath: CSRF_TOKEN_PATH,
    refreshSessionIntervalFn: (session: Session): number => {
      return session.sessionexpires > 0 ? (session.sessionexpires * 1000) / 2 : DEFAULT_SESSION_REFRESH_INTERVAL_MS;
    },
    getAdditionalPermissions,
  });

  const i18nProvider = useMemo(() => createI18nProviderWithBrowserLocale(getMessages), []);

  const dataProvider = useCreateOrdDataProvider({
    methodsCall: (axiosInstance: AxiosInstance): Partial<IngordDataProvider> => ({
      getUserSettings: async () => getUserSettings(axiosInstance, getRealmId()),
      updateUserSettings: async (params) => updateUserSettings(axiosInstance, params, getRealmId()),
      getTotp: async () => getTotp(axiosInstance, getRealmId()),
      createTotp: async (params) => createTotp(axiosInstance, params, getRealmId()),
      totpVerify: async (params) => totpVerify(axiosInstance, params, getRealmId()),
      updatePassword: async (params) => updatePassword(axiosInstance, params, getRealmId()),
      downloadFile: async (params) => downloadFile(axiosInstance, params, getRealmId()),
      getUserVoicemail: async (userId) => getUserVoicemail(axiosInstance, getRealmId(), userId),
      createUserVoicemail: async (userId, params) => createUserVoicemail(axiosInstance, getRealmId(), userId, params),
      updateUserVoicemail: async (userId, params) => updateUserVoicemail(axiosInstance, getRealmId(), userId, params),
      getPersonalVoicemail: async () => getPersonalVoicemail(axiosInstance, getRealmId()),
      updatePersonalVoicemail: async (params) => updatePersonalVoicemail(axiosInstance, getRealmId(), params),
      updateAutoprovisioningAuthentication: async (params) =>
        updateAutoprovisioningAuthentication(axiosInstance, getRealmId(), params),
      updateAutoprovisioningSettings: async (params) =>
        updateAutoprovisioningSettings(axiosInstance, getRealmId(), params),
      getApTemplateDefaults: async () => getApTemplateDefaults(axiosInstance, getRealmId()),
      createBackup: async (domainId) => createBackup(axiosInstance, domainId, getRealmId()),
      createBackupImport: async (realm) => createBackupImport(axiosInstance, getRealmId(), realm),
      createBackupRestore: async (targetDomainId) => createBackupRestore(axiosInstance, getRealmId(), targetDomainId),
    }),
    callbacks: [
      apGroupCallbacks,
      sipIdentityCallbacks,
      callflowCallbacks,
      calendarEventCallbacks,
      personalCalendarEventCallbacks,
      contactFavoritesCallback,
    ],
    dataProviderMatcher: (defaultDataProvider, axiosInstance) => (resource) => {
      switch (resource) {
        case `me/${CalendarEvents}`:
        case `${CalendarEvents}`:
          return createCalendarEventsDataProvider(defaultDataProvider);
        case Resources.user_import:
          return createImportDataProvider(defaultDataProvider, getRealmId(), axiosInstance);
        case Resources.tasks:
          return createTasksDataProvider(defaultDataProvider, getRealmId(), axiosInstance);
        default:
          return defaultDataProvider;
      }
    },
  });

  const router = useMemo(() => {
    const basename = process.env.REACT_APP_INGORD_BASE_URL ?? process.env.PUBLIC_URL;
    return createBrowserRouter(
      [
        {
          path: '*',
          element: (
            <ResourcesContext.Provider value={Resources}>
              <NavigateHelper />
              <Admin
                disableTelemetry
                dashboard={HomePage}
                authProvider={authProvider}
                dataProvider={dataProvider}
                i18nProvider={i18nProvider}
                loginPage={loginPage}
                {...props}
              >
                {ALL_RESOURCES}
                {ROUTES_ELEMENT}
              </Admin>
            </ResourcesContext.Provider>
          ),
        },
      ],
      { basename }
    );
  }, [authProvider, dataProvider, i18nProvider, loginPage, props]);

  return <RouterProvider router={router} />;
};
